import * as React from "react";
import {Display} from "../components/Display";
import {VeboxOverlay} from "../components/overlay/VeboxOverlay";

import "./processes.scss";
import {Button} from "@vebox/etui-core";

interface ProcessContextType {
    position: number;
    goTo: (position: number) => void;
    reachablePosition: number;
    setReachablePosition: (position: number) => void;
    total: number;
}

const ProcessContext = React.createContext<ProcessContextType>({} as any);

interface ProcessPageProps {
    total: number
}

export const ProcessPage: React.FC<ProcessPageProps & React.HTMLAttributes<any>> = ({total, children, ...divPops}) => {

    const [positions, setPositions] = React.useState({
        position: 0,
        reachablePosition: 0,
    });

    return (
        <div {...divPops} className={"process-page-wrapper " + (divPops.className ?? "")}>
            <ProcessContext.Provider
                value={{
                    ...positions,
                    total,
                    goTo: newPosition => setPositions({
                        position: newPosition,
                        reachablePosition: Math.max(newPosition, positions.reachablePosition),
                    }),
                    setReachablePosition: newPosition => setPositions({
                        position: Math.min(newPosition, positions.position),
                        reachablePosition: newPosition,
                    })
                }}
            >
                {children}
            </ProcessContext.Provider>
        </div>
    );
}

export const useProcessController = () => {
    return React.useContext(ProcessContext);
}

interface ProcessHeaderProps {
    processTitle: string;
}

export const ProcessHeader: React.FC<ProcessHeaderProps> = (props) => {
    return (
        <div className={"process-header flx-row space-between"}>
            <div className={"left-side"}>
                <h1 className={"process-title"}>{props.processTitle}</h1>
                {props.children}
            </div>
            <div className={"right-side"}/>
        </div>
    );
}

export interface ProcessIndicatorProps {
    onChange?: (position: number) => void;
    position?: number;
    reachablePosition?: number;
}

export const ProcessIndicator: React.FC<ProcessIndicatorProps> = (props) => {

    const {position, reachablePosition, goTo} = React.useContext(ProcessContext);

    const changePosition = (p: number) => {
        return () => {
            if (props.onChange) {
                props.onChange(p);
            }
            if (goTo && p <= reachablePosition) {
                goTo(p);
            }
        };
    };

    return (
        <div className={"process-indicator flx-row"}>
            {(props?.children as any[]).map((element, index) => (
                <>
                    <Display isVisible={index !== 0}>
                        <div className={"step-line " + (index <= (props.reachablePosition ?? reachablePosition) ? "passed-step-line" : "inactive-step-line")}/>
                    </Display>
                    {
                        React.cloneElement<ProcessIndicatorStepProps>(
                            element,
                            {
                                ...element.props,
                                onClick: changePosition(index),
                                order: index === 0
                                    ? ProcessStageOrder.FIRST
                                    : index === (props.children as any[]).length - 1
                                        ? ProcessStageOrder.LAST
                                        : ProcessStageOrder.MIDDLE,
                                indicatorPosition: (props.position ?? position),
                                reachablePosition,
                                stepPosition: index,
                            },
                        )
                    }
                </>
            ))}
        </div>
    );
}

export interface ProcessIndicatorStepProps {
    onClick?: () => void;
    order?: ProcessStageOrder;
    stepPosition?: number; // 0 -> n - 1
    reachablePosition?: number;
    indicatorPosition?: number;
}

export const ProcessIndicatorStep: React.FC<ProcessIndicatorStepProps> = (props) => {

    const labelClass = props.order === ProcessStageOrder.FIRST
        ? "first-label"
        : props.order === ProcessStageOrder.LAST
            ? "last-label" : "middle-label"

    const markerClass = props.stepPosition < props.indicatorPosition
        ? "passed-step"
        : props.stepPosition <= props.reachablePosition
            ? "current-step"
            : "future-step";

    return (
        <div
            className={"process-indicator-step flx-col"}
            onClick={props.onClick}
        >
            <div className={"process-indicator-step-marker centered " + markerClass}>
                <Display isVisible={props.stepPosition >= props.indicatorPosition}>
                    {props.stepPosition + 1}
                </Display>
                <Display isVisible={props.stepPosition < props.indicatorPosition}>
                    <span className={"material-icons"}>done</span>
                </Display>
            </div>
            <div className={"process-indicator-step-label " + labelClass}>
                {props.children}
            </div>
        </div>
    );
}

export enum ProcessStageOrder { FIRST, MIDDLE, LAST}

export interface ProcessStageProps {
    stepNumber: number;
    title: string | JSX.Element;
    stageOrder: ProcessStageOrder;
    bodyStyles?: React.CSSProperties;
}

export const ProcessStage: React.FC<ProcessStageProps> = (props) => {

    const {position} = React.useContext(ProcessContext);

    const {targetProps, show, clear} = VeboxOverlay.useTarget(<div className={"grey-overlay"}/>);

    React.useEffect(() => {
        if (position + 1 === props.stepNumber) {
            clear();
        } else {
            show();
        }
    }, [position]);

    const firstLineVisible = props.stageOrder === ProcessStageOrder.LAST || props.stageOrder === ProcessStageOrder.MIDDLE;
    const lastLineVisible = props.stageOrder === ProcessStageOrder.FIRST || props.stageOrder === ProcessStageOrder.MIDDLE;

    return (
        <VeboxOverlay.Target {...targetProps} className={"process-stage flx-col"}>
            <div className={"process-stage-header flx-row flx-align-center"}>
                <div className={"header-decoration"} style={{visibility: !firstLineVisible ? "hidden" : undefined}}/>
                <div className={"step-title-wrapper flx-row flx-align-center"}>
                    <div className={"step-number-circle centered"}> {props.stepNumber} </div>
                    <div className={"step-title"}> {props.title} </div>
                </div>
                <div className={"header-decoration"} style={{visibility: !lastLineVisible ? "hidden" : undefined}}/>
            </div>
            <div className={"process-stage-body centered"} style={props?.bodyStyles}>
                {props.children}
            </div>
        </VeboxOverlay.Target>
    )
}

export enum ProcessStageArrowDirection { BACKWARD, FORWARD }

interface ProcessStageArrowProps {
    stepNumber: number;
    direction: ProcessStageArrowDirection;
    visible?: boolean;
}

export const ProcessStageArrow: React.FC<ProcessStageArrowProps> = (props) => {

    const {position, reachablePosition, goTo, total} = useProcessController();

    const move = _ => {
        switch (props.direction) {
            case ProcessStageArrowDirection.BACKWARD: {
                goTo((props.stepNumber - 1) - 1);
                break;
            }
            case ProcessStageArrowDirection.FORWARD: {
                goTo((props.stepNumber - 1) + 1);
                break;
            }
        }
    };

    const isVisible = ((props.direction === ProcessStageArrowDirection.FORWARD) && ((props.stepNumber - 1) < reachablePosition) && (props.stepNumber < total))
        || (props.direction === ProcessStageArrowDirection.BACKWARD && props.stepNumber > 1);

    return (
        <>
            {(position + 1) === props.stepNumber
                && (
                    <div
                        className={"centered"}
                        style={{visibility: (props.visible ?? isVisible) ? "visible" : "hidden", padding: 5}}
                    >
                        <Button onClick={move} variant={"tertiary"} shape={"circle"} style={{padding: 16}}>
                            <span className="material-icons">
                                {props.direction === ProcessStageArrowDirection.FORWARD ? "arrow_forward" : "arrow_back"}
                            </span>
                        </Button>
                    </div>
                )
            }
        </>
    );
}